import { useAssociationFeedInfinite } from 'api/queries/associations/feed';
import { FeedItemDto, EventHostedFeedItemData } from 'api/dtos/associations/feed';
import { EventItem } from 'components/EventsCalendar/types';
import { useRouter } from 'next/router';
import React from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'components/Button';
import EventCard from 'components/EventsList/components/EventCard';
import EventsListSkeleton from 'components/EventsList/components/EventsListSkeleton';

const AssocEventsList = ({
  associationId,
  user
}: {
  associationId: string;
  user?: any;
}) => {
  const { t } = useTranslation(['associations', 'organizations']);
  const router = useRouter();
  const { 
    data: events, 
    isLoading: isLoadingEvents,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useAssociationFeedInfinite({
    associationId,
    useUnauthenticatedEndpoint: !user
  });

  // Helper function to transform feed item to event
  const transformFeedToEvent = (item: FeedItemDto): EventItem => {
    const eventData = item.data as EventHostedFeedItemData;
    const creator = item.creator;
    
    return {
      _id: item.refId || '',
      id: eventData.eventId,
      photo: { url: eventData.media?.url || '', position: 'center' },
      title: eventData.title,
      startDate: eventData.startDate?.toString() || new Date().toString(),
      isDraft: false,
      host: creator ? {
        _id: creator.id,
        id: creator.id,
        name: `${creator.firstName} ${creator.lastName}`,
        firstName: creator.firstName,
        lastName: creator.lastName,
        photo: { url: creator.photoUrl || '' }
      } : {
        _id: '', id: '', name: 'Unknown', firstName: 'Unknown', lastName: '', photo: { url: '' }
      },
      cohosts: eventData.cohosts || [],
      groups: eventData.groups.map(g => ({
        _id: g.id,
        id: g.id,
        name: g.name,
        description: '',
        badgeImageUrl: g.badgeImageUrl || '',
        coverImageUrl: ''
      } as const)),
      totalGuests: eventData.guestsCount || 0,
      guestPreview: eventData.guestsPreview || [],
      isOrganizer: item.metadata?.currentUser?.isOrganizer || false
    };
  };

  const eventItems = events?.pages
    ?.flatMap(page => page.items)
    .filter(item => item.type === 'eventHosted')
    .map(transformFeedToEvent) || [];

  const handleNavigateToEvent = (event: EventItem) =>
    router.push(`/events/${event.id}`);

  if (isLoadingEvents) {
    return <EventsListSkeleton />;
  }

  return (
    <div className="z-10 w-full md:px-8 md:mx-auto md:max-w-[1200px]">
      {eventItems?.length > 0 ? (
        <>
          <div
            className="flex flex-col md:flex-row gap-8 md:gap-5 justify-center w-full flex-wrap"
          >
            {eventItems.map((event) => (
              <EventCard
                key={event.id}
                event={event}
                handleNavigateToEvent={handleNavigateToEvent}
                organization={event.groups[0]}
              />
            ))}
          </div>
          {hasNextPage && (
            <Button
              variant="secondary"
              className="w-[125px] self-center mx-auto mt-4"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
            >
              <span className="flex items-center justify-center gap-2">
                {isFetchingNextPage && (
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                )}
                {isFetchingNextPage ? '' : t('organizations:events.loadMore')}
              </span>
            </Button>
          )}
        </>
      ) : (
        <p className="flex justify-center text-lg text-gray-500 pt-8">
          {t('events.noEventsFound')}
        </p>
      )}
    </div>
  );
};

export default AssocEventsList;
